import React, { useCallback, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useGetUserQuery } from '../services/userApi';
import { StorageKeys } from '../utils/storage';
import TopBar from '../components/TopBar';
import SettingsBars from '../components/SettingsBars';
import { useCameras } from '../hooks';
import { Flex } from '../components/shared/Flex';
import media from '../style/media';

import { useGetPatientByIdQuery } from '../services/patientApi';
import { useGetOrganizationByIdQuery } from '../services/organizationApi';
import { PageContainer, ContentWrapper, Header, Title, Subtitle, GlassCard, Grid, Field, FieldLabel, FieldValue } from '../style/layout';
import { Shield, Key, FileText, CheckCircle } from 'lucide-react';

const AccessDeniedMessage = styled.div`
  text-align: center;
  padding: 3rem;
  background-color: rgba(254, 242, 242, 0.8);
  border: 1px solid rgba(254, 202, 202, 0.5);
  border-radius: 24px;
  color: #991b1b;

  h3 {
    color: #991b1b;
    margin-top: 0;
    font-size: 1.5rem;
    font-weight: 700;
  }

  p {
    margin: 0.75rem 0;
    font-size: 1rem;
    opacity: 0.9;
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  color: #3b82f6;
  transition: all 0.3s ease;

  ${Field}:hover & {
    background: #f1f5f9;
    transform: scale(1.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const LicenseInfo: React.FC = () => {
  const nav = useNavigate();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isLicenseValid, setIsLicenseValid] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const cameras = useCameras();
  const user = useMemo(() => {
    const userStr = localStorage.getItem(StorageKeys.AUTH_USER);
    return userStr ? JSON.parse(userStr) : null;
  }, []);
  const isOrgType3 = user?.org_type === 3;
  const orgId = user?.org_id;
  const pid = useMemo(() => localStorage.getItem(StorageKeys.PATIENT_ID) || '', []);

  const { data: patientData, isLoading: patientLoading, isError: patientError, error: pError } = useGetPatientByIdQuery(pid, { skip: !pid || isOrgType3 });
  const { data: orgData, isLoading: orgLoading, isError: orgError, error: oError } = useGetOrganizationByIdQuery(orgId, { skip: !orgId || !isOrgType3 });

  const isLoading = isOrgType3 ? orgLoading : patientLoading;
  const isError = isOrgType3 ? orgError : patientError;
  const error = isOrgType3 ? oError : pError;

  const displayData = useMemo(() => {
    if (isOrgType3) {
      return {
        license_no: orgData?.organization?.license_no,
        limit_type: orgData?.organization?.limit_type?.toString(),
        status: orgData?.organization?.status?.toString(),
        access_code: '-',
        available_count: orgData?.organization?.available_limit
      };
    }
    return {
      license_no: patientData?.patient?.license_no,
      limit_type: patientData?.patient?.limit_type?.toString(),
      status: patientData?.patient?.status?.toString(),
      access_code: patientData?.patient?.access_code,
      available_count: patientData?.patient?.available_count
    };
  }, [isOrgType3, orgData, patientData]);

  const hasData = isOrgType3 ? !!orgData : !!patientData;

  useEffect(() => {
    const referrer = document.referrer;
    const isFromSettings = referrer.includes('/settings') ||
      referrer === '' ||
      referrer.includes(window.location.origin);

    if (!isFromSettings) {
      setShowAccessDenied(true);
      const timer = setTimeout(() => {
        nav('/settings');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [nav]);

  const toggleSettings = useCallback(() => {
    setIsSettingsOpen(prev => !prev);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
  }, []);

  const updateLicenseStatus = useCallback((valid: boolean) => {
    setIsLicenseValid(valid);
  }, []);


  return (
    <PageContainer>
      <TopBar onSettingsClick={toggleSettings} isMeasuring={false} />
      <SettingsBars
        open={isSettingsOpen}
        onClose={handleCloseSettings}
        cameras={cameras}
        isLicenseValid={isLicenseValid}
      />
      <ContentWrapper>
        {showAccessDenied ? (
          <GlassCard>
            <AccessDeniedMessage>
              <h3>Access Denied</h3>
              <p>Please access Profile through the Settings page.</p>
              <p>Redirecting you back to Settings...</p>
            </AccessDeniedMessage>
          </GlassCard>
        ) : (
          <GlassCard>
            <Header>
              <Title>License Info</Title>
              <Subtitle>Your license status and access information</Subtitle>
            </Header>

            {isLoading && (
              <Flex center padding="4rem" width="100%">
                <p style={{ color: '#64748b', fontWeight: 500 }}>Loading licensing data...</p>
              </Flex>
            )}

            {isError && (
              <AccessDeniedMessage>
                <h3>Error Loading Data</h3>
                <p>{(error as any)?.data?.message || 'Failed to load licensing details.'}</p>
              </AccessDeniedMessage>
            )}

            {hasData && (
              <Grid>
                <Field>
                  <IconWrapper style={{ color: '#3b82f6' }}>
                    <Shield />
                  </IconWrapper>
                  <FieldLabel>License Number</FieldLabel>
                  <FieldValue>{displayData.license_no || '-'}</FieldValue>
                </Field>

                <Field>
                  <IconWrapper style={{ color: '#10b981' }}>
                    <FileText />
                  </IconWrapper>
                  <FieldLabel>License Type</FieldLabel>
                  <FieldValue>
                    {displayData.limit_type == '1'
                      ? 'Limited Access'
                      : displayData.status == '2'
                        ? 'Unlimited Access'
                        : 'N/A'}
                  </FieldValue>
                </Field>

                <Field>
                  <IconWrapper style={{ color: '#f59e0b' }}>
                    <Key />
                  </IconWrapper>
                  <FieldLabel>Access Code</FieldLabel>
                  <FieldValue>{displayData.access_code || '-'}</FieldValue>
                </Field>

                <Field>
                  <IconWrapper style={{ color: '#ec4899' }}>
                    <CheckCircle />
                  </IconWrapper>
                  <FieldLabel>Available Count</FieldLabel>
                  <FieldValue>{displayData.available_count ?? '-'}</FieldValue>
                </Field>
              </Grid>
            )}
          </GlassCard>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default LicenseInfo;
